import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Modal from "react-modal";
import { MdCheckCircle, MdSubject, MdTitle } from "react-icons/md";

import { useAuth } from "contexts/AuthContext";
import { isSubscribed } from "utils/subscriptions.utils";
import { generateReportUrl } from "utils/report.utils";
import { RootState } from "redux/reducers";
import { modalStyles } from "styles/modals";
import { NamedReport } from "types/search";
import {
  formatCount,
  highlight,
} from "components/Header/ChildComponents/SearchDropdown";

import BuyReport from "components/Common/forms/BuyReport";
import Pagination from "../../../../Common/misc/Pagination";

Modal.setAppElement("#root");

type ResultsProps = {
  categories: string[];
  setCategories: Function;
};

type Chapter = {
  chapter_number: number;
  title: string;
  matches: number;
};

type Item = {
  category_name: string;
  chapters: Chapter[];
  matches: number;
  report_id: number;
  report_name: string;
  report_year: number;
};

// Chapter chips shown before "+N more"
const SHOWN_CHAPTERS = 3;

// Reports matched by name shown before "Show all"
const SHOWN_NAMED = 5;

// Rows like the search dropdown's: the report, its match count and the chapters
// the matches are in. The row opens the report at the chapter with the most
// matches; a chip opens its own chapter. Unsubscribed rows have Buy Now, which
// opens the report page's "Requesting to Buy" form here. Above them, like the
// dropdown, the reports whose name matches.
const SearchResults: React.FC<ResultsProps> = ({
  categories,
  setCategories,
}) => {
  const { currentUser } = useAuth();
  const { search, subscriptions } = useSelector((state: RootState) => state);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [buying, setBuying] = useState<NamedReport | null>(null);
  const [allNamed, setAllNamed] = useState(false);
  const term = search.correctedQuery || search.query;
  // Not in a search saved before named reports existed
  const namedReports: NamedReport[] = search.namedReports || [];

  useEffect(() => {
    setAllNamed(false);
  }, [search.query]);

  const handleSort = (a, b) => {
    const { sortBy } = search.filters;

    switch (sortBy) {
      case "new":
        return b.report_year - a.report_year;
      case "old":
        return a.report_year - b.report_year;
      case "category":
        if (a.category_name.toLowerCase() < b.category_name.toLowerCase()) {
          return -1;
        }
        if (a.category_name.toLowerCase() > b.category_name.toLowerCase()) {
          return 1;
        }
        return 0;
      case "subscribed": {
        const aSubscribed = isSubscribed(subscriptions, a.report_id, currentUser, "active");
        const bSubscribed = isSubscribed(subscriptions, b.report_id, currentUser, "active");
        if (aSubscribed !== bSubscribed) return aSubscribed ? -1 : 1;
        return b.report_year - a.report_year;
      }
      default:
        // chapters
        return b.chapters.length - a.chapters.length;
    }
  };

  const handleFilter = (item: { category_name: string }) => {
    const { category } = search.filters;
    let display = {
      categories: true,
    };

    if (category !== "all") {
      display.categories = item.category_name === category;
    }

    if (!Object.values(display).includes(false)) {
      return item;
    }
  };

  // The name matches in the chosen category, in their own order
  const named = namedReports.filter(handleFilter);
  const shownNamed = allNamed ? named : named.slice(0, SHOWN_NAMED);

  // The category menu also lists categories found only among the name matches
  useEffect(() => {
    const missing = namedReports
      .map((report) => report.category_name)
      .filter(
        (category, i, all) =>
          category && all.indexOf(category) === i && !categories.includes(category)
      );
    if (missing.length) setCategories([...categories, ...missing]);
  }, [namedReports, categories, setCategories]);

  const toggleChapters = (reportId: number) => {
    setExpanded(
      expanded.includes(reportId)
        ? expanded.filter((id) => id !== reportId)
        : [...expanded, reportId]
    );
  };

  return (
    <>
      {named.length > 0 && (
        <Named>
          <GroupLabel>
            <MdTitle aria-hidden="true" />
            <span>
              Found in report names ·{" "}
              {formatCount(named.length, "report")}
            </span>
          </GroupLabel>
          <List>
            {shownNamed.map((report) => {
              const subscribed = isSubscribed(
                subscriptions,
                report.report_id,
                currentUser,
                "active"
              );

              return (
                <Row key={report.report_id}>
                  <RowTop>
                    <RowText>
                      <RowTitle
                        to={`/reports/${report.report_id}/${generateReportUrl(
                          report.report_name
                        )}/1`}
                        state={{ backUrl: "/catalog/search" }}
                        target="_blank"
                      >
                        {highlight(report.report_name, term)}
                      </RowTitle>
                      <RowMeta>
                        <span>
                          {report.category_name} · {report.report_year}
                        </span>
                        <span aria-hidden="true">·</span>
                        {subscribed ? (
                          <Subscribed>
                            <MdCheckCircle aria-hidden="true" />
                            Subscribed
                          </Subscribed>
                        ) : (
                          <span>Not subscribed</span>
                        )}
                      </RowMeta>
                    </RowText>
                    {!subscribed && (
                      <RowTotal>
                        <BuyButton type="button" onClick={() => setBuying(report)}>
                          Buy Now
                        </BuyButton>
                      </RowTotal>
                    )}
                  </RowTop>
                </Row>
              );
            })}
          </List>
          {named.length > SHOWN_NAMED && (
            <ShowAll>
              <More type="button" onClick={() => setAllNamed(!allNamed)}>
                {allNamed ? "Show fewer" : `Show all ${named.length}`}
              </More>
            </ShowAll>
          )}
          {search.results.length > 0 && (
            <ChaptersLabel>
              <MdSubject aria-hidden="true" />
              <span>
                Found in chapters ·{" "}
                {formatCount(search.results.length, "report")}
              </span>
            </ChaptersLabel>
          )}
        </Named>
      )}
      {/* With only name matches, no "No reports available" under them */}
      {(search.results.length > 0 || named.length === 0) && (
        <List>
          <Pagination limit={10}>
            {search.results
              .sort(handleSort)
              .filter(handleFilter)
              .map((item: Item) => {
                const {
                  report_id,
                  report_name,
                  report_year,
                  category_name,
                  chapters,
                  matches,
                } = item;
                const subscribed = isSubscribed(
                  subscriptions,
                  report_id,
                  currentUser,
                  "active"
                );
                const url = generateReportUrl(report_name);
                const chapterUrl = (chapter: number) =>
                  `/reports/${report_id}/${url}/${chapter}?term=${encodeURIComponent(
                    search.query
                  )}&mode=${search.mode || "exact"}`;
                const open = expanded.includes(report_id);
                const shown = open ? chapters : chapters.slice(0, SHOWN_CHAPTERS);
                const more = chapters.length - SHOWN_CHAPTERS;

                // collect unique categories and regions from result
                if (categories.indexOf(item.category_name) === -1) {
                  setCategories([...categories, item.category_name]);
                }

                return (
                  <Row key={report_id}>
                    <RowTop>
                      <RowText>
                        <RowTitle
                          to={chapterUrl(chapters[0].chapter_number)}
                          state={{ backUrl: "/catalog/search" }}
                          target="_blank"
                        >
                          {highlight(report_name, term)}
                        </RowTitle>
                        <RowMeta>
                          <span>
                            {category_name} · {report_year}
                          </span>
                          <span aria-hidden="true">·</span>
                          {subscribed ? (
                            <Subscribed>
                              <MdCheckCircle aria-hidden="true" />
                              Subscribed
                            </Subscribed>
                          ) : (
                            <span>Not subscribed</span>
                          )}
                        </RowMeta>
                      </RowText>
                      <RowTotal>
                        <strong>{matches.toLocaleString("en-US")}</strong>
                        <span>{matches === 1 ? "match" : "matches"}</span>
                        {!subscribed && (
                          <BuyButton type="button" onClick={() => setBuying(item)}>
                            Buy Now
                          </BuyButton>
                        )}
                      </RowTotal>
                    </RowTop>
                    <RowChapters>
                      <span>Found in {formatCount(chapters.length, "chapter")}</span>
                      {shown.map((chapter) => {
                        const title =
                          chapter.title || `Chapter ${chapter.chapter_number}`;

                        return (
                          <Chip
                            key={chapter.chapter_number}
                            to={chapterUrl(chapter.chapter_number)}
                            state={{ backUrl: "/catalog/search" }}
                            target="_blank"
                            title={title}
                          >
                            <ChipTitle>{title}</ChipTitle>
                            <strong>{chapter.matches.toLocaleString("en-US")}</strong>
                          </Chip>
                        );
                      })}
                      {more > 0 && (
                        <More type="button" onClick={() => toggleChapters(report_id)}>
                          {open ? "Show fewer" : `+${more} more`}
                        </More>
                      )}
                    </RowChapters>
                  </Row>
                );
              })}
          </Pagination>
        </List>
      )}
      <Modal
        onRequestClose={() => setBuying(null)}
        isOpen={!!buying}
        contentLabel="Buy Report"
        style={modalStyles}
      >
        {buying && (
          <BuyReport
            setToggleModal={() => setBuying(null)}
            report_name={buying.report_name}
            report_year={String(buying.report_year)}
            report_id={buying.report_id}
          />
        )}
      </Modal>
    </>
  );
};

const List = styled.ul`
  margin: 0;
  border-top: 1px solid ${(props) => props.theme.colors.grey};
`;

// The reports matched by name, above the chapter results
const Named = styled.div`
  margin-bottom: 1.2rem;
`;

// A tinted band with an icon over each group, as in the dropdown
const GroupLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 2rem;
  background-color: #eef3f7;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.idataDarkBlue};

  svg {
    flex: none;
    width: 2rem;
    height: 2rem;
  }
`;

const ChaptersLabel = styled(GroupLabel)`
  margin-top: 2.4rem;
`;

const ShowAll = styled.div`
  padding: 1rem 2rem 0;
  font-size: 1.6rem;
`;

const Row = styled.li`
  position: relative;
  padding: 1.6rem 2rem 1.8rem;
  border-bottom: 1px solid #eef0f2;
  color: #14202e;
  text-align: left;

  /* A blue tint and a bar on the left, as on the dropdown's rows */
  &:hover,
  &:focus-within {
    background-color: #eaf4fa;
    box-shadow: inset 3px 0 0 ${(props) => props.theme.colors.idataBlue};
  }
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const RowText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

// Its ::after covers the row, so the whole row opens the report
const RowTitle = styled(Link)`
  font-size: 2.2rem;
  line-height: 1.35;
  color: #14202e;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
  }

  &:hover,
  &:focus-visible,
  ${Row}:hover & {
    color: ${(props) => props.theme.colors.idataBlue};
    opacity: 1;
    outline: none;
  }
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem;
  margin-top: 0.4rem;
  font-size: 1.7rem;
  color: #5b6573;
`;

const Subscribed = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${(props) => props.theme.colors.idataDarkBlue};
  font-weight: 600;

  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const RowTotal = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  strong {
    font-size: 3rem;
    font-weight: 700;
    line-height: 1.15;
    color: ${(props) => props.theme.colors.idataDarkBlue};
  }

  span {
    font-size: 1.5rem;
    color: #5b6573;
  }
`;

// Above the title's cover, so it opens the form instead of the report
const BuyButton = styled.button`
  position: relative;
  z-index: 1;
  margin-top: 0.6rem;
  height: 3rem;
  padding: 0 1.4rem;
  background-color: #fff;
  border: 1px solid ${(props) => props.theme.colors.idataBlue};
  border-radius: 999px;
  color: ${(props) => props.theme.colors.idataBlue};
  font-family: inherit;
  font-size: 1.5rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background-color: ${(props) => props.theme.colors.idataBlue};
    color: #fff;
    outline: none;
  }
`;

const RowChapters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem;
  margin-top: 1.2rem;
  font-size: 1.6rem;
  color: #5b6573;
`;

// Above the title's cover, so it opens its own chapter
const Chip = styled(Link)`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  max-width: 100%;
  height: 3.4rem;
  padding: 0 1.4rem;
  background-color: #eef3f7;
  border-radius: 999px;
  color: #14202e;

  strong {
    color: ${(props) => props.theme.colors.idataDarkBlue};
  }

  /* white on the hovered row, whose tint is close to the chip's colour */
  ${Row}:hover &,
  ${Row}:focus-within & {
    background-color: #fff;
  }

  ${Row} &:hover,
  ${Row} &:focus-visible {
    background-color: ${(props) => props.theme.colors.idataBlue};
    color: #fff;
    opacity: 1;
    outline: none;

    strong {
      color: #fff;
    }
  }
`;

const ChipTitle = styled.span`
  max-width: 36rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const More = styled.button`
  position: relative;
  z-index: 1;
  padding: 0;
  border-radius: 0;
  color: ${(props) => props.theme.colors.idataDarkBlue};
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

export default SearchResults;
